import { useCallback } from 'react';
import { flushSync } from 'react-dom';

/**
 * Returns a function used to wrap setState calls in a view transition call.
 * Avoid using in loops or other frequent operations, and batch as many setState calls as possible into a single view transition.
 *
 * Example usage:
 * ```js
 * const viewTransition = useViewTransition();
 *
 * const myUpdateFn = () => {
 *   viewTransition(() => {
 *     setSomeState1(123)
 *     setSomeState2('abc')
 *   })
 * }
 * ```
 *
 * @returns {Function} A function used to wrap setState calls that triggers a view transition
 */
export const useViewTransition = () => {
  const updateState = useCallback(updateFn => {
    if (document.startViewTransition) document.startViewTransition(() => flushSync(() => updateFn()));
    else updateFn();
  }, []);

  return updateState;
};
